import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Chip,
  alpha,
  useTheme,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';

const PricingSection = () => {
  const { t } = useTranslation();
  const theme = useTheme();

  const plans = [
    {
      name: t('starter_plan'),
      price: '29',
      period: t('per_month'),
      description: 'Perfect for small businesses and startups',
      features: [
        t('up_to_stores', { count: 2 }),
        '100 invoices per month',
        t('peppol_invoicing'),
        t('basic_inventory'),
        t('pos_system'),
        t('email_support'),
        'Basic reporting',
      ],
      popular: false,
      color: theme.palette.primary.main,
    },
    {
      name: t('professional_plan'),
      price: '79',
      period: t('per_month'),
      description: 'For growing businesses with multiple locations',
      features: [
        t('up_to_stores', { count: 10 }),
        'Unlimited invoices',
        t('peppol_invoicing'),
        t('advanced_inventory'),
        t('pos_system'),
        t('priority_support'),
        t('advanced_analytics'),
        'Online store integration',
        t('api_access'),
      ],
      popular: true,
      color: theme.palette.success.main,
    },
    {
      name: t('enterprise_plan'),
      price: 'Custom',
      period: t('contact_sales'),
      description: 'Tailored solutions for large organizations',
      features: [
        t('unlimited_stores'),
        'Unlimited invoices',
        t('peppol_invoicing'),
        t('custom_integrations'),
        t('dedicated_account_manager'),
        '24/7 phone support',
        'Custom reporting',
        'White-label options',
        t('sla_support'),
      ],
      popular: false,
      color: theme.palette.secondary.main,
    },
  ];

  return (
    <Box sx={{ py: 10, backgroundColor: theme.palette.background.default }}>
      <Container maxWidth="lg">
        <Box sx={{ textAlign: 'center', mb: 8 }}>
          <Typography
            variant="h3"
            sx={{
              fontWeight: 700,
              mb: 2,
              fontSize: { xs: '1.6rem', md: '2.1rem' },
              color: theme.palette.text.primary,
            }}
          >
            {t('pricing_section_title')}
          </Typography>
          <Typography
            variant="h6"
            sx={{
              color: theme.palette.text.secondary,
              maxWidth: 700,
              mx: 'auto',
              lineHeight: 1.6,
              fontSize: { xs: '0.95rem', md: '1.05rem' },
            }}
          >
            {t('pricing_section_subtitle')}
          </Typography>
        </Box>

        <Grid container spacing={4} alignItems="stretch">
          {plans.map((plan, index) => (
            <Grid item xs={12} md={4} key={index}>
              <Card
                sx={{
                  height: '100%',
                  borderRadius: 3,
                  position: 'relative',
                  transition: 'all 0.3s',
                  border: plan.popular ? `2px solid ${plan.color}` : '1px solid',
                  borderColor: plan.popular ? plan.color : 'divider',
                  '&:hover': {
                    transform: 'translateY(-8px)',
                    boxShadow: `0 12px 24px ${alpha(plan.color, 0.2)}`,
                  },
                }}
              >
                {plan.popular && (
                  <Chip
                    label={t('most_popular')}
                    sx={{
                      position: 'absolute',
                      top: 16,
                      right: 16,
                      backgroundColor: plan.color,
                      color: '#fff',
                      fontWeight: 600,
                    }}
                  />
                )}
                <CardContent sx={{ p: 4 }}>
                  <Typography variant="h5" sx={{ fontWeight: 600, mb: 1 }}>
                    {plan.name}
                  </Typography>
                  <Typography variant="body2" sx={{ color: theme.palette.text.secondary, mb: 3 }}>
                    {plan.description}
                  </Typography>

                  <Box sx={{ mb: 3 }}>
                    <Box sx={{ display: 'flex', alignItems: 'baseline', mb: 1 }}>
                      {plan.price !== 'Custom' && (
                        <Typography
                          variant="h6"
                          sx={{
                            color: theme.palette.text.secondary,
                            mr: 0.5,
                          }}
                        >
                          €
                        </Typography>
                      )}
                      <Typography
                        variant="h3"
                        sx={{
                          fontWeight: 700,
                          color: plan.color,
                        }}
                      >
                        {plan.price}
                      </Typography>
                    </Box>
                    <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
                      {plan.period}
                    </Typography>
                  </Box>

                  <Button
                    fullWidth
                    variant={plan.popular ? 'contained' : 'outlined'}
                    size="large"
                    sx={{
                      mb: 3,
                      borderRadius: 2,
                      py: 1.5,
                      fontWeight: 600,
                      textTransform: 'none',
                      backgroundColor: plan.popular ? plan.color : 'transparent',
                      borderColor: plan.color,
                      color: plan.popular ? '#fff' : plan.color,
                      '&:hover': {
                        backgroundColor: plan.popular ? plan.color : alpha(plan.color, 0.1),
                        borderColor: plan.color,
                      },
                    }}
                  >
                    {plan.price === 'Custom' ? t('contact_sales') : t('choose_plan')}
                  </Button>

                  <List sx={{ p: 0 }}>
                    {plan.features.map((feature, idx) => (
                      <ListItem key={idx} sx={{ px: 0, py: 0.5 }}>
                        <ListItemIcon sx={{ minWidth: 32 }}>
                          <CheckCircleIcon sx={{ fontSize: 20, color: plan.color }} />
                        </ListItemIcon>
                        <ListItemText
                          primary={feature}
                          primaryTypographyProps={{
                            variant: 'body2',
                            color: theme.palette.text.secondary,
                          }}
                        />
                      </ListItem>
                    ))}
                  </List>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Container>
    </Box>
  );
};

export default PricingSection;
